import React from 'react';
import { motion } from 'framer-motion';

/**
 * HairColorSelector Component
 * 
 * Displays solid color swatches (NO people or hair images)
 * Selection only updates state - does NOT trigger generation
 * Simple color selection only
 */

const HairColorSelector = ({ 
  colors, 
  selectedColorId, 
  onColorSelect, 
  disabled = false 
}) => {
  const handleColorClick = (color) => {
    if (disabled) return;
    
    try {
      // Update parent state
      onColorSelect(color.id);

      // Persist to localStorage
      localStorage.setItem('hs_color_mode', color.id === 'no-change' ? 'no_change' : 'preset');
      if (color.promptText) {
        localStorage.setItem('hs_color_value', color.promptText);
      }
    } catch (error) {
      console.error('Error in handleColorClick:', error);
      // Don't throw - just log the error
    }
  };

  return (
    <div className="space-y-3">
      {/* Color Grid */}
      <div className="grid grid-cols-4 gap-2">
        {colors.map((color) => {
          const isSelected = selectedColorId === color.id;
          
          // Special handling for "No Change" and "Custom" options
          if (color.id === 'no-change') {
            return (
              <motion.button
                key={color.id}
                onClick={() => handleColorClick(color)}
                disabled={disabled}
                className={`
                  aspect-square rounded-lg flex items-center justify-center text-xs font-medium
                  border-2 transition-all duration-200
                  ${isSelected 
                    ? 'border-[#7d6633] bg-[#7d6633]/10 text-[#7d6633] dark:text-[#a0824a]' 
                    : 'border-zinc-300 dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-500'}
                `}
                whileHover={!disabled ? { scale: 1.05 } : {}}
                whileTap={!disabled ? { scale: 0.95 } : {}}
              >
                <div className="text-center px-1">
                  <div className="text-lg mb-1">🔄</div>
                  <div className="text-[10px] leading-tight">Original</div>
                </div>
              </motion.button>
            );
          }

          // Regular color swatch with solid color
          return (
            <motion.button
              key={color.id}
              onClick={() => handleColorClick(color)}
              disabled={disabled}
              className={`
                relative aspect-square rounded-lg overflow-hidden
                border-2 transition-all duration-200
                ${isSelected 
                  ? 'border-[#7d6633] ring-2 ring-[#7d6633]/30' 
                  : 'border-zinc-300 dark:border-zinc-600'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-500'}
              `}
              whileHover={!disabled ? { scale: 1.05 } : {}}
              whileTap={!disabled ? { scale: 0.95 } : {}}
            >
              {/* Solid color swatch */}
              <div 
                className="w-full h-full"
                style={{ backgroundColor: color.colorHex }}
              />

              {/* Label overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm py-1 px-1">
                <p className="text-[10px] text-white font-medium text-center leading-tight">
                  {color.displayName}
                </p>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-1 right-1 bg-[#7d6633] text-white rounded-full p-0.5">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </motion.button>
        );
      })}
      </div>
    </div>
  );
};

export default HairColorSelector;

